/**
 * InlineVideoEmbed — one component that plays a creator's reel or video
 * inline (in-place) for whichever platform the URL points to.
 *
 * v134 (Feb 2026) — supports YouTube (long-form + shorts), Instagram
 * (reel / p / tv), TikTok, and direct MP4/WebM/Ogg files. Falls back
 * to a "Watch on <platform>" link when the URL isn't embeddable (X,
 * arbitrary URL, etc.).
 *
 * Used inside:
 *  - StorefrontBuyModal (v134 — replaces the old "Watch reel" pill so
 *    buyers can watch without leaving checkout)
 *  - Public bio hub `video` block (upgraded to accept IG/TikTok URLs)
 *  - Product / Coupon blocks (inline preview instead of link-out)
 */
import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { WatchReelButton } from "./WatchReelButton";
import { api } from "../lib/api";

// Session id for anonymous video-engagement tracking. Stored in
// localStorage so a single buyer's play + purchase can be joined
// per-session on the funnel report.
function getVideoSessionId() {
  try {
    let sid = localStorage.getItem("linkola:vs");
    if (!sid) {
      sid = "vs_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem("linkola:vs", sid);
    }
    return sid;
  } catch { return "vs_anon_" + Date.now(); }
}

async function logVideoEvent({ event, url, platform, surface, product_id, coupon_id, block_id }) {
  try {
    await api.post("/analytics/video/event", {
      event, url, platform, surface,
      product_id, coupon_id, block_id,
      session_id: getVideoSessionId(),
    });
  } catch { /* best-effort — failures are silent */ }
}

/**
 * Parse the URL and return an embed descriptor:
 *  { kind: "iframe", src, platform }   for oEmbed-friendly platforms
 *  { kind: "video",  src }              for direct video files
 *  null                                 for anything we can't embed
 */
export function parseEmbed(url) {
  if (!url) return null;
  const u = String(url).trim();

  // Direct video file.
  if (/\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(u)) {
    return { kind: "video", src: u, platform: "file" };
  }

  // YouTube — normal, shorts, youtu.be short-links. Domain-anchored so
  // an unrelated URL containing `?v=xxx` doesn't get mis-detected.
  const yShort = u.match(/(?:^|\/\/)(?:www\.)?youtu\.be\/([\w-]{6,})/i);
  const yWatch = u.match(/(?:^|\/\/)(?:www\.)?youtube\.com\/[^\s]*[?&]v=([\w-]{6,})/i);
  const yShorts = u.match(/(?:^|\/\/)(?:www\.)?youtube\.com\/shorts\/([\w-]{6,})/i);
  const yEmbed = u.match(/(?:^|\/\/)(?:www\.)?youtube\.com\/embed\/([\w-]{6,})/i);
  const yId = (yShort && yShort[1]) || (yWatch && yWatch[1])
    || (yShorts && yShorts[1]) || (yEmbed && yEmbed[1]);
  if (yId) return { kind: "iframe", src: `https://www.youtube.com/embed/${yId}?rel=0`, platform: "youtube" };

  // Instagram — reel / p (post) / tv (IGTV).
  const igMatch = u.match(/instagram\.com\/(reel|reels|p|tv)\/([\w-]+)/i);
  if (igMatch) {
    const path = igMatch[1] === "reels" ? "reel" : igMatch[1];
    return {
      kind: "iframe",
      src: `https://www.instagram.com/${path}/${igMatch[2]}/embed/`,
      platform: "instagram",
      // Instagram embeds default to a portrait 9:16-ish aspect.
      aspect: "portrait",
    };
  }

  // TikTok — /@user/video/{id} or /t/{id}
  const ttVideo = u.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/i);
  const ttShort = u.match(/tiktok\.com\/t\/([\w]+)/i);
  const ttId = (ttVideo && ttVideo[1]) || (ttShort && ttShort[1]);
  if (ttId) return {
    kind: "iframe",
    src: `https://www.tiktok.com/embed/v2/${ttId}`,
    platform: "tiktok",
    aspect: "portrait",
  };

  // X / Twitter — the widget requires JS, oEmbed isn't a plain iframe.
  // Fall back to a link.
  return null;
}

/**
 * v149.5 — Best-effort automatic cover for an embed descriptor.
 *
 * YouTube is the only platform with a stable, unauthenticated thumbnail
 * URL. Instagram's oEmbed moved behind a Meta app token, and TikTok's
 * needs a network round-trip, so both return null here and the caller
 * falls back to rendering its own play-card rather than a blank box.
 */
export function embedThumbnail(parsed) {
  if (!parsed || parsed.platform !== "youtube") return null;
  const m = parsed.src.match(/\/embed\/([\w-]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

/**
 * v149.5 — Media-fragment hint for self-hosted files that have no cover.
 *
 * `#t=0.1` asks the browser to seek to 0.1s, which makes it decode and
 * paint a real frame instead of leaving an empty box. Skipped when the
 * URL already carries a fragment, and pointless (so skipped) when a
 * poster image is supplied — the poster wins either way.
 */
function withFirstFrameHint(src) {
  if (!src || src.includes("#")) return src;
  return `${src}#t=0.1`;
}

// Aspect (as a padding-top percentage) for the embed frame. Shared by the
// cover placeholder and the real iframe so swapping one for the other on
// tap never shifts the page.
function embedPaddingTop(parsed, aspect) {
  if (aspect === "vertical") return "177.78%";   // 9:16 — YouTube Shorts
  if (parsed.platform === "instagram") return "178%";  // reel + caption strip
  if (parsed.platform === "tiktok") return "165%";
  if (parsed.aspect === "portrait") return "125%";
  return "56.25%";                                // 16:9 default
}

export function InlineVideoEmbed({
  url,
  testid = "inline-video-embed",
  className = "",
  // v135 — Optional tracking context; when provided, a `play` event is
  // logged so creators can see a Video → Purchase funnel per item.
  productId = null,
  couponId = null,
  blockId = null,
  surface = "inline",
  // v143.13 (Feb 2026) — Optional callback fired once, on first play.
  // Used by ActionBlockRenderer to also record a `click` event on the
  // shared action_page_events pipeline so the Results drawer shows the
  // right count. Kept separate from the video-analytics log above so
  // the two stay decoupled.
  onPlay = null,
  // v149 — Quick Promote ads mount the player after a "tap to play", so
  // YouTube starts straight away (muted, as browsers require) and Shorts
  // get a 9:16 frame ("vertical"). Omitted → previous behaviour.
  autoplay = false,
  aspect = null,
  // v149.5 — Cover / thumbnail support. Both are OPT-IN so every existing
  // call site keeps its current behaviour untouched.
  //   posterUrl : creator-uploaded cover image. Used as the <video>
  //               poster for self-hosted files, and as the tap-to-play
  //               cover for third-party embeds.
  //   cover     : when true, an iframe embed is gated behind a cover +
  //               play button instead of mounting the third-party frame
  //               straight away. Without a cover the block used to be a
  //               tall empty box whenever the embed failed to resolve
  //               (removed / private / region-blocked reels).
  posterUrl = null,
  cover = false,
}) {
  const parsed = parseEmbed(url);
  const wrapRef = useRef(null);
  const firedPlayRef = useRef(false);
  const [covered, setCovered] = useState(cover);
  // v149.20 — Cover sources that failed to LOAD. A creator's uploaded cover
  // is stored as an absolute URL (the upload button bakes in
  // REACT_APP_BACKEND_URL), so it can stop resolving later — moved host,
  // deleted file, blocked origin. Before this, a dead cover hid its <img>
  // and nothing took its place: `coverSrc` was truthy, so the play-card
  // branch never ran and the block rendered as a plain black rectangle.
  // That is the "video is blank even though I uploaded a cover" report.
  // Tracking the dead source lets the chain fall through properly:
  // custom cover -> platform thumbnail -> play card.
  const [deadCovers, setDeadCovers] = useState([]);
  const markCoverDead = (src) =>
    setDeadCovers((prev) => (!src || prev.includes(src) ? prev : [...prev, src]));
  // A new video (or a freshly uploaded cover) re-arms every candidate.
  useEffect(() => { setDeadCovers([]); }, [url, posterUrl]);
  const posterAlive = posterUrl && !deadCovers.includes(posterUrl) ? posterUrl : null;

  // `<video poster>` fires no error event when the image 404s — the element
  // just shows an empty frame. Probe the cover so a dead one falls back to
  // the first-frame hint instead of a black box. Same URL the poster would
  // fetch, so this shares the browser cache rather than doubling traffic.
  useEffect(() => {
    if (!posterUrl || !parsed || parsed.kind !== "video") return;
    let alive = true;
    const probe = new Image();
    probe.onerror = () => { if (alive) markCoverDead(posterUrl); };
    probe.src = posterUrl;
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posterUrl, parsed && parsed.kind]);

  // Re-arm the cover when the block switches to a different video (or the
  // creator toggles a cover on in the editor) — otherwise the live preview
  // would stay stuck on the player from the previous URL.
  useEffect(() => { setCovered(cover); }, [cover, url]);

  const firePlay = () => {
    if (firedPlayRef.current || !parsed) return;
    firedPlayRef.current = true;
    logVideoEvent({
      event: "play",
      url,
      platform: parsed.platform,
      surface,
      product_id: productId,
      coupon_id: couponId,
      block_id: blockId,
    });
    // Fire the caller's onPlay hook too (best-effort — a throw here
    // must never break the video embed itself).
    try { if (onPlay) onPlay(); } catch { /* ignore */ }
  };

  // For platforms without a reliable JS API (IG, TikTok), we log the
  // event when the embed first becomes visible in the viewport — a
  // reasonable proxy for "buyer saw the reel". YouTube uses the same
  // signal as a fallback; the native postMessage listener below fires
  // first when the user hits play.
  //
  // v149.5 — `wrapRef` is attached to whichever of the two nodes is on
  // screen: the cover placeholder, or the iframe frame once the cover
  // has been tapped. `covered` is in the dep list so the observer
  // re-attaches across that swap. Without both, gating the iframe
  // behind a cover would have silently moved this event off the dwell
  // signal and onto the tap — a tracking change nobody asked for.
  useEffect(() => {
    if (!parsed || parsed.kind !== "iframe" || !wrapRef.current) return;
    const el = wrapRef.current;
    const obs = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          // 500ms dwell before firing — filters flash-scrolls past.
          const t = setTimeout(firePlay, 500);
          en.target._dwell = t;
        } else if (en.target._dwell) {
          clearTimeout(en.target._dwell);
          en.target._dwell = null;
        }
      }
    }, { threshold: [0, 0.5, 1] });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed && parsed.src, covered]);

  // YouTube — listen for postMessage `onStateChange` with state 1 (play).
  useEffect(() => {
    if (!parsed || parsed.platform !== "youtube") return;
    const onMsg = (ev) => {
      if (!ev.data) return;
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (data.event === "onStateChange" && data.info === 1) firePlay();
      } catch { /* not our message */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed && parsed.src]);

  if (!parsed) {
    return url
      ? <WatchReelButton url={url} testid={`${testid}-fallback`} />
      : null;
  }

  if (parsed.kind === "video") {
    // A creator-supplied poster always wins. Without one we ask for a
    // 0.1s seek so the browser paints a real frame rather than an empty
    // black box. Both are inert unless `cover`/`posterUrl` are passed,
    // so other surfaces render exactly as before.
    const fileSrc = (cover && !posterAlive) ? withFirstFrameHint(parsed.src) : parsed.src;
    return (
      <video
        src={fileSrc}
        poster={posterAlive || undefined}
        preload={cover ? "metadata" : undefined}
        controls
        playsInline
        onPlay={firePlay}
        className={`w-full border-2 border-ink bg-black ${className}`}
        data-testid={testid}
        data-platform="file"
        data-has-poster={posterAlive ? "1" : "0"}
      />
    );
  }

  // Instagram embeds ship a reel + caption strip beneath. 125% only
  // shows the reel; the caption/handle text then gets clipped by our
  // overflow:hidden wrapper. Give IG (and TikTok, similar layout) more
  // vertical room so nothing falls outside the block. The cover
  // placeholder below reuses the same value so tapping play never
  // shifts the page.
  const paddingTop = embedPaddingTop(parsed, aspect);

  // v149.5 — Cover gate. Custom cover first, then the automatic one
  // (YouTube only), then our own play-card. Anything beats the tall
  // empty frame this used to render.
  if (covered) {
    // Precedence: creator's cover -> platform thumbnail -> our play card.
    // Anything that has already failed to load is skipped, so a dead cover
    // reveals the next option rather than a black rectangle.
    const coverSrc = [posterUrl, embedThumbnail(parsed)]
      .filter(Boolean)
      .find((src) => !deadCovers.includes(src)) || null;
    const platformLabel = parsed.platform === "instagram"
      ? "Reel"
      : parsed.platform === "tiktok" ? "TikTok" : "Video";
    return (
      <button
        ref={wrapRef}
        type="button"
        // No firePlay() here on purpose. The `play` event stays on the
        // viewport-dwell signal below exactly as it was before covers
        // existed, so this fix changes what the block LOOKS like and
        // nothing about what it REPORTS.
        onClick={() => setCovered(false)}
        aria-label={`Play ${platformLabel.toLowerCase()}`}
        className={`w-full block relative border-2 border-ink bg-black overflow-hidden ${className}`}
        style={{ paddingTop }}
        data-testid={`${testid}-cover`}
        data-platform={parsed.platform}
        data-cover-kind={posterUrl ? "custom" : coverSrc ? "auto" : "placeholder"}
      >
        {coverSrc ? (
          <img
            src={coverSrc}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            // A dead thumbnail (deleted upload, YouTube 404) must not leave
            // a black hole. Record it so the next candidate — the platform
            // thumbnail, else the play card — renders on the next pass.
            onError={() => markCoverDead(coverSrc)}
            data-testid={`${testid}-cover-img`}
          />
        ) : (
          <span
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg, var(--ah-accent, #FF3B30) 0%, #FF8A5B 100%)" }}
          />
        )}
        <span className="absolute inset-0 bg-black/20" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-16 h-16 rounded-full border-2 border-ink bg-white flex items-center justify-center shadow-brutalSm">
            <Play className="w-7 h-7 fill-ink ml-1" strokeWidth={2} />
          </span>
        </span>
        <span className="absolute bottom-2 left-2 border-2 border-ink bg-white px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider">
          {platformLabel}
        </span>
      </button>
    );
  }

  // enablejsapi=1 so YouTube posts state changes back to us.
  // `cover` implies the frame is mounted by a deliberate tap, so YouTube
  // should start immediately (muted, as browsers require) exactly like
  // the explicit `autoplay` caller.
  const wantsAutoplay = autoplay || cover;
  const src = parsed.platform === "youtube"
    ? `${parsed.src}${parsed.src.includes("?") ? "&" : "?"}enablejsapi=1${wantsAutoplay ? "&autoplay=1&mute=1&playsinline=1" : ""}`
    : parsed.src;
  return (
    <div
      ref={wrapRef}
      className={`w-full border-2 border-ink bg-black overflow-hidden ${className}`}
      style={{ position: "relative", paddingTop }}
      data-testid={testid}
      data-platform={parsed.platform}
    >
      <iframe
        src={src}
        title="Video"
        loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

export default InlineVideoEmbed;
